import React, {useLayoutEffect, useRef, useState, ReactNode} from "react";
import {useTranslation} from "react-i18next";
import { SlickNextArrow, SlickPrevArrow } from "./content/MultiMediaSlider";
import { SliderWrapper } from "./ReactSlickLazy";
import { useLiveblogWrapperClassNames } from "./LiveblogWrapper";



interface LiveblogHighlightSliderSummaryProps {
  highlightCount: number
  children: ReactNode
}

export function LiveblogHighlightSliderSummary({highlightCount, children}: LiveblogHighlightSliderSummaryProps) {
  const {t} = useTranslation("liveblog")
  const [lastSlides, setLastSlides] = useState<boolean>();
  const [firstSlide, setFirstSlide] = useState<boolean>(true);
  const hostRef = useRef<HTMLDivElement|null>(null);
  const [showAndScroll, setShowAndScroll] = useState<{show:number, scroll:number}>({show: 1, scroll: 1});
  // the wrapper's breakpoint, taken from context rather than observed independently: the
  // wrapper commits its breakpoint class and this context value in the same render, so
  // keying the effect below on it catches a breakpoint change at the moment it actually
  // lands, instead of racing the wrapper's own resize-driven re-render (see design.md).
  const {breakpoint} = useLiveblogWrapperClassNames();

  const readIntVar = (el: HTMLElement, name: string) => {
    const v = getComputedStyle(el).getPropertyValue(name).trim();
    const n = parseInt(v, 10);
    return Number.isFinite(n) && n > 0 ? n : undefined;
  }

  useLayoutEffect(() => {
    const el = hostRef.current;
    if (!el) return;

    const applyFromCss = () => {
      const slides = readIntVar(el, '--t4-hss-slides') ?? 1;
      const scroll = readIntVar(el, '--t4-hss-scroll') ?? slides;
      const clampedSlides = highlightCount ? Math.min(slides, highlightCount) : slides;
      setShowAndScroll(prev => (prev.show !== clampedSlides || prev.scroll !== scroll)
        ? { show: clampedSlides, scroll }
        : prev
      );
    };

    const ro = new ResizeObserver(() => {
      applyFromCss();
    });
    ro.observe(el);

    // Initial reads with fallbacks in case external CSS loads slightly later
    applyFromCss();
    const rafId = window.requestAnimationFrame(() => applyFromCss());
    const timeoutId = window.setTimeout(() => applyFromCss(), 120);
    const onLoad = () => applyFromCss();
    window.addEventListener('load', onLoad, { once: true });

    return () => {
      ro.disconnect();
      window.cancelAnimationFrame(rafId);
      window.clearTimeout(timeoutId);
      window.removeEventListener('load', onLoad as any);
    };
  }, [highlightCount, breakpoint]);

  if (!highlightCount) {
    return <></>;
  }

  const slidesToShow = showAndScroll.show;
  const slidesToScroll = showAndScroll.scroll ?? showAndScroll.show;
  const slideActive = highlightCount && slidesToShow < highlightCount;

  const possibleLastIndex =  Math.max(0, (highlightCount || 0) - slidesToShow);

  const settings = {
    adaptiveHeight: false,
    infinite: false,
    speed: 150,
    slidesToShow: slidesToShow,
    slidesToScroll: slidesToScroll,
    className: `
      tik4-sl tik4-hss__sl
      ${lastSlides ? 'tik4-hss__sl--last-slides' : ''}
      ${firstSlide ? 'tik4-hss__sl--first-slide' : ''}
      ${slideActive ? 'tik4-hss__sl--slide-active' : ''}
      `,
    nextArrow: <SlickNextArrow />,
    prevArrow: <SlickPrevArrow />,
    dots: true,
    appendDots: (dots:React.ReactNode) => <ul className="tik4-sl__d">{dots}</ul>,
        dotsClass: "tik4-sl__d",
    customPaging: (i:number) => (
      <button className="tik4-sl__d__d">
        <div className="tik4-sl__d__d__i">{i+1}</div>
      </button>
    ),
    beforeChange: (current:any, next:any) => {
      setLastSlides(next >= possibleLastIndex);
      setFirstSlide(next === 0);
    }
  }

  return <React.Fragment>
    {highlightCount && highlightCount > 0 &&
    <div
      className="tik4-hss"
      ref={hostRef}
    >
      <div className="tik4-hss__label"><span className="tik4-hs__label__t">{t('liveblog.highlight_summary.headline')}</span></div>
      <SliderWrapper {...settings} >
        {children}
      </SliderWrapper>
    </div>
    }
  </React.Fragment>
}
